import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Trash2, Flame, Utensils, GlassWater, Wine, Droplets, CheckCircle2, Scale, TrendingUp, TrendingDown, Target, StickyNote, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { computeTarget, isToday, getDietaryWarnings } from "@/lib/mbNutrition";
import { deleteMeal, loadMeals, loadWater, WATER_TARGET, updateMeal, recordDailyCalories, loadDailyHistory, loadWeightHistory, recordWeight, getWaterStreak, recordWaterGoal } from "@/lib/mbStorage";
import { Beverages } from "@/components/Beverages";
import { RecipeSheet } from "@/components/RecipeSheet";
import { MealDetail } from "@/components/MealDetail";

const mealColor = (type) => {
  const t = (type || "").toLowerCase();
  if (t.includes("colazione")) return "#f59e0b";
  if (t.includes("pranzo")) return "#3b82f6";
  if (t.includes("cena")) return "#8b5cf6";
  return "#2cd4bf";
};

const fmtTime = (ts) =>
  new Date(ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
const fmtDate = (ts) =>
  new Date(ts).toLocaleDateString("it-IT", { day: "numeric", month: "short" });

// Grafico leggero: calorie giornaliere degli ultimi 7 giorni con linea target e media settimanale.
const CaloriesTrend = ({ meals, target }) => {
  const DAY = 86400000;
  const t0 = new Date();
  t0.setHours(0, 0, 0, 0);
  const todayStart = t0.getTime();

  const liveByDay = {};
  meals.forEach((m) => {
    const d = new Date(m.timestamp);
    d.setHours(0, 0, 0, 0);
    const k = d.getTime();
    liveByDay[k] = (liveByDay[k] || 0) + (m.calories || 0);
  });

  const days = loadDailyHistory(7).map((h) => ({
    dayStart: h.dayStart,
    cals: liveByDay[h.dayStart] != null ? liveByDay[h.dayStart] : h.cals,
    label: h.dayStart === todayStart ? "Oggi" : new Date(h.dayStart).toLocaleDateString("it-IT", { weekday: "narrow" }),
  }));

  const logged = days.filter((d) => d.cals > 0);
  const avg = logged.length ? Math.round(logged.reduce((s, d) => s + d.cals, 0) / logged.length) : 0;
  const overAvg = avg > target;
  const chartMax = Math.max(target, ...days.map((d) => d.cals), 1) * 1.2;
  const targetPct = Math.min(100, (target / chartMax) * 100);

  return (
    <div
      className="bg-white rounded-2xl p-4 mb-4 shadow-[0_4px_12px_rgba(0,0,0,0.03)] border border-slate-200"
      data-testid="calories-trend"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-sm font-bold text-slate-800">Andamento Calorie</h3>
        <span className="text-[10px] font-semibold text-[#2b90ef] bg-[#e0f2fe] px-2 py-0.5 rounded-full">7 giorni</span>
      </div>
      <div className="relative h-28 flex items-end justify-between gap-1.5 px-0.5">
        {/* Linea target */}
        <div
          className="absolute left-0 right-0 border-t-2 border-dashed border-[#f59e0b] z-10 pointer-events-none"
          style={{ bottom: `${targetPct}%` }}
        >
          <span className="absolute -top-4 right-0 text-[9px] font-bold text-[#f59e0b] bg-white px-1 rounded" data-testid="trend-target">
            Target {target}
          </span>
        </div>
        {days.map((d, i) => {
          const hPct = Math.min(100, (d.cals / chartMax) * 100);
          const over = d.cals > target;
          const isToday = d.dayStart === todayStart;
          return (
            <div key={`day-${d.dayStart}`} className="flex-1 h-full flex flex-col items-center justify-end" data-testid={`trend-bar-${i}`}>
              <span className="text-[8px] font-semibold text-slate-500 mb-0.5 h-2.5" data-testid={`trend-cal-${i}`}>{d.cals > 0 ? d.cals : ""}</span>
              <motion.div
                className={`w-full max-w-[24px] rounded-t-md ${isToday ? "ring-2 ring-[#2b90ef]/30" : ""}`}
                style={{ backgroundColor: d.cals === 0 ? "#e2e8f0" : over ? "#f59e0b" : "#2b90ef" }}
                initial={{ height: 0 }}
                animate={{ height: `${Math.max(hPct, d.cals > 0 ? 4 : 2)}%` }}
                transition={{ duration: 0.5, ease: "easeOut", delay: i * 0.05 }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between gap-1.5 px-0.5 mt-1.5">
        {days.map((d, i) => (
          <span key={`lbl-${d.dayStart}`} className={`flex-1 text-center text-[10px] font-semibold capitalize ${d.dayStart === todayStart ? "text-[#2b90ef]" : "text-slate-400"}`}>{d.label}</span>
        ))}
      </div>
      <div className="flex items-center justify-between border-t border-slate-100 mt-3 pt-2.5">
        <div className="flex flex-col">
          <span className="text-[11px] text-slate-500">Media settimanale</span>
          <span className="text-[9px] text-slate-400" data-testid="trend-average-days">media su {logged.length}/7 giorni</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-display text-sm font-bold text-slate-800" data-testid="trend-average">{avg}<span className="text-[10px] font-medium text-slate-400 ml-0.5">kcal</span></span>
          <span
            className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${overAvg ? "bg-amber-50 text-[#d97706]" : "bg-emerald-50 text-emerald-600"}`}
            data-testid="trend-average-status"
          >
            {overAvg ? "Sopra target" : "Sotto target"}
          </span>
        </div>
      </div>
    </div>
  );
};

const CONFETTI_COLORS = ["#2b90ef", "#2cd4bf", "#f59e0b", "#ef4444", "#8b5cf6"];
const Confetti = () => (
  <div className="fixed inset-0 z-[999] pointer-events-none overflow-hidden" data-testid="weight-celebration">
    {Array.from({ length: 28 }).map((_, i) => {
      const left = Math.random() * 100;
      const delay = Math.random() * 0.35;
      const dur = 1.6 + Math.random() * 1.3;
      const size = 6 + Math.random() * 7;
      return (
        <motion.span
          key={`conf-${i}`}
          initial={{ top: "-8%", opacity: 1, rotate: 0 }}
          animate={{ top: "110%", opacity: 0, rotate: 420 }}
          transition={{ duration: dur, delay, ease: "easeIn" }}
          style={{ position: "absolute", left: `${left}%`, width: size, height: size * 0.5, backgroundColor: CONFETTI_COLORS[i % CONFETTI_COLORS.length], borderRadius: 2 }}
        />
      );
    })}
  </div>
);

// Grafico a linea del peso degli ultimi 7 giorni con obiettivo e registrazione rapida.
const WeightTrend = ({ profile, onProfileChange }) => {
  const t0 = new Date();
  t0.setHours(0, 0, 0, 0);
  const todayStart = t0.getTime();
  const [input, setInput] = useState(String(profile.weight || ""));
  const [goalInput, setGoalInput] = useState(String(profile.weightGoal || ""));
  const [celebrate, setCelebrate] = useState(false);

  useEffect(() => {
    if (!celebrate) return;
    const timer = setTimeout(() => setCelebrate(false), 2600);
    return () => clearTimeout(timer);
  }, [celebrate]);

  const goal = parseFloat(String(goalInput).replace(",", ".")) || 0;

  const hist = loadWeightHistory(7).map((h) =>
    h.dayStart === todayStart ? { ...h, kg: profile.weight || h.kg } : h
  );
  const logged = hist.filter((h) => h.kg != null);
  const kgs = logged.map((h) => h.kg);
  const scaleVals = [...kgs];
  if (goal > 0) scaleVals.push(goal);
  const minKg = scaleVals.length ? Math.min(...scaleVals) : 0;
  const maxKg = scaleVals.length ? Math.max(...scaleVals) : 0;
  const padding = Math.max((maxKg - minKg) * 0.3, 1);
  const lo = minKg - padding;
  const span = maxKg + padding - lo || 1;

  const coords = hist.map((h, i) => ({
    ...h,
    x: hist.length > 1 ? (i / (hist.length - 1)) * 100 : 50,
    y: h.kg != null ? 100 - ((h.kg - lo) / span) * 100 : null,
  }));
  const linePts = coords.filter((c) => c.y != null);
  const poly = linePts.map((c) => `${c.x},${c.y}`).join(" ");
  const goalY = goal > 0 ? Math.min(100, Math.max(0, 100 - ((goal - lo) / span) * 100)) : null;

  const current = profile.weight || 0;
  const first = logged.length ? logged[0].kg : null;
  const delta = first != null && logged.length > 1 ? Math.round((current - first) * 10) / 10 : 0;
  const DeltaIcon = delta < 0 ? TrendingDown : delta > 0 ? TrendingUp : null;
  const deltaColor = delta < 0 ? "text-emerald-600 bg-emerald-50" : delta > 0 ? "text-[#d97706] bg-amber-50" : "text-slate-500 bg-slate-100";

  const toGoal = goal > 0 && current > 0 ? Math.round((current - goal) * 10) / 10 : null;
  const baseline = first != null ? first : current;
  const losingDir = baseline >= goal;
  const goalReached = goal > 0 && current > 0 && (losingDir ? current <= goal : current >= goal);

  const saveWeight = () => {
    const v = parseFloat(String(input).replace(",", "."));
    if (!v || v <= 0) {
      toast.error("Inserisci un peso valido");
      return;
    }
    const rounded = Math.round(v * 10) / 10;
    const prev = profile.weight || 0;
    const wasReached = goal > 0 && prev > 0 && (losingDir ? prev <= goal : prev >= goal);
    const nowReached = goal > 0 && (losingDir ? rounded <= goal : rounded >= goal);
    onProfileChange({ ...profile, weight: rounded });
    if (nowReached && !wasReached) {
      setCelebrate(true);
      toast.success("🎉 Traguardo raggiunto!", { description: `Hai raggiunto il tuo obiettivo di ${goal} kg. Complimenti!` });
    } else {
      toast.success("Peso registrato", { description: `${rounded} kg` });
    }
  };

  const commitGoal = (val) => {
    setGoalInput(val);
    const v = parseFloat(String(val).replace(",", "."));
    if (v > 0) onProfileChange({ ...profile, weightGoal: Math.round(v * 10) / 10 });
  };

  return (
    <div className="bg-white rounded-2xl p-4 mb-4 shadow-[0_4px_12px_rgba(0,0,0,0.03)] border border-slate-200" data-testid="weight-trend">
      {celebrate && <Confetti />}
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-sm font-bold text-slate-800 flex items-center gap-1.5">
          <Scale size={15} className="text-[#2cd4bf]" /> Andamento Peso
        </h3>
        <span className="text-[10px] font-semibold text-[#2b90ef] bg-[#e0f2fe] px-2 py-0.5 rounded-full">7 giorni</span>
      </div>

      <div className="flex items-end justify-between mb-1">
        <div>
          <span className="font-display text-2xl font-bold text-slate-800" data-testid="weight-current">{current || "--"}</span>
          <span className="text-[11px] text-slate-400 ml-1">kg oggi</span>
        </div>
        {delta !== 0 && (
          <span className={`text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 ${deltaColor}`} data-testid="weight-delta">
            {DeltaIcon && <DeltaIcon size={12} />}
            {delta > 0 ? "+" : ""}{delta} kg settimana
          </span>
        )}
      </div>

      {/* Obiettivo peso */}
      <div className="flex items-center gap-2 mb-2 text-[11px]">
        <span className="text-slate-500 flex items-center gap-1"><Target size={12} className="text-[#f59e0b]" /> Obiettivo</span>
        <input
          type="number"
          step="0.1"
          min="0"
          inputMode="decimal"
          value={goalInput}
          onChange={(e) => commitGoal(e.target.value)}
          data-testid="weight-goal-input"
          className="w-16 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-[12px] font-semibold text-slate-800 outline-none focus:border-[#f59e0b]"
        />
        <span className="text-slate-400">kg</span>
        {toGoal != null && (
          goalReached ? (
            <span className="ml-auto text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full" data-testid="weight-goal-reached">Traguardo raggiunto 🎯</span>
          ) : (
            <span className="ml-auto text-[10px] font-bold text-slate-500" data-testid="weight-to-goal">
              {toGoal > 0 ? `mancano ${toGoal} kg` : `sotto di ${Math.abs(toGoal)} kg`}
            </span>
          )
        )}
      </div>

      {linePts.length > 0 ? (
        <>
          <div className="relative w-full h-24 mt-1">
            {/* Linea obiettivo */}
            {goalY != null && (
              <div className="absolute left-0 right-0 border-t-2 border-dashed border-[#f59e0b] z-10 pointer-events-none" style={{ top: `${goalY}%` }}>
                <span className="absolute -top-4 right-0 text-[9px] font-bold text-[#f59e0b] bg-white px-1 rounded" data-testid="weight-goal-line">Obiettivo {goal}</span>
              </div>
            )}
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full overflow-visible">
              {linePts.length > 1 && (
                <polyline points={poly} fill="none" stroke="#2cd4bf" strokeWidth="2.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
              )}
            </svg>
            {linePts.map((c) => (
              <div
                key={`pt-${c.dayStart}`}
                className={`absolute w-2.5 h-2.5 rounded-full border-2 border-white -translate-x-1/2 -translate-y-1/2 z-20 ${c.dayStart === todayStart ? "bg-[#2b90ef] ring-2 ring-[#2b90ef]/25" : "bg-[#0f766e]"}`}
                style={{ left: `${c.x}%`, top: `${c.y}%` }}
                data-testid={c.dayStart === todayStart ? "weight-point-today" : undefined}
              />
            ))}
          </div>
          <div className="flex justify-between gap-1 px-0.5 mt-1.5">
            {coords.map((c) => (
              <span key={`wl-${c.dayStart}`} className={`flex-1 text-center text-[10px] font-semibold capitalize ${c.dayStart === todayStart ? "text-[#2b90ef]" : "text-slate-400"}`}>
                {c.dayStart === todayStart ? "Oggi" : new Date(c.dayStart).toLocaleDateString("it-IT", { weekday: "narrow" })}
              </span>
            ))}
          </div>
        </>
      ) : (
        <p className="text-center text-[12px] text-slate-400 italic py-6" data-testid="weight-empty">Registra il tuo peso per iniziare a vedere l'andamento.</p>
      )}

      <div className="flex gap-2 border-t border-slate-100 mt-3 pt-3">
        <div className="flex-1 relative">
          <input
            type="number"
            step="0.1"
            min="0"
            inputMode="decimal"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            data-testid="weight-input"
            placeholder="Peso di oggi"
            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-3 pr-8 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-[#2b90ef]"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-slate-400 font-medium">kg</span>
        </div>
        <button
          onClick={saveWeight}
          data-testid="weight-save-btn"
          className="px-4 py-2.5 bg-[#2cd4bf] text-white rounded-xl font-semibold text-sm active:scale-[0.98] transition-transform"
        >
          Registra
        </button>
      </div>
    </div>
  );
};

export const Diario = ({ profile, meals, onProfileChange, onMealsChange }) => {
  const [recipeMeal, setRecipeMeal] = useState(null);
  const [detailMeal, setDetailMeal] = useState(null);
  const [water, setWaterCount] = useState(loadWater());
  const glassesLeft = Math.max(0, WATER_TARGET - water);
  const hydrationReached = water >= WATER_TARGET;
  const waterStreak = getWaterStreak();
  const target = computeTarget(profile);
  const consumed = meals.filter((m) => isToday(m.timestamp)).reduce((s, m) => s + (m.calories || 0), 0);
  const remaining = Math.max(0, target - consumed);
  const pct = Math.min(100, Math.round((consumed / target) * 100));
  const barColor = pct >= 90 ? "#f59e0b" : "#2cd4bf";

  const todayMeals = meals.filter((m) => isToday(m.timestamp));
  const macro = todayMeals.reduce(
    (a, m) => ({ p: a.p + (m.protein || 0), c: a.c + (m.carbs || 0), f: a.f + (m.fat || 0) }),
    { p: 0, c: 0, f: 0 }
  );
  const kc = { c: macro.c * 4, f: macro.f * 9, p: macro.p * 4 };
  const totalKc = kc.c + kc.f + kc.p || 1;
  const drinks = todayMeals.filter((m) => m.is_drink || m.kind === "drink" || m.meal_type === "Bevanda");
  const drinkKcal = drinks.reduce((s, m) => s + (m.calories || 0), 0);
  const alcoholG = drinks.reduce((s, m) => s + (m.alcohol_g || 0), 0);
  const macroBars = [
    { key: "c", label: "Carbo", g: macro.c, color: "#f59e0b" },
    { key: "f", label: "Grassi", g: macro.f, color: "#3b82f6" },
    { key: "p", label: "Prot", g: macro.p, color: "#ef4444" },
  ];

  const handleDelete = (id) => {
    const updated = deleteMeal(id);
    onMealsChange(updated.sort((a, b) => b.timestamp - a.timestamp));
  };

  useEffect(() => {
    recordDailyCalories();
  }, [meals]);

  useEffect(() => {
    if (water >= WATER_TARGET) recordWaterGoal();
  }, [water]);

  useEffect(() => {
    recordWeight(profile.weight);
  }, [profile.weight]);

  const handleMealEdit = (id, patch) => {
    const updated = updateMeal(id, patch);
    onMealsChange(updated);
    setDetailMeal((prev) => (prev && prev.id === id ? { ...prev, ...patch } : prev));
    recordDailyCalories();
  };

  return (
    <div className="w-full" data-testid="screen-diario">
      <div className="text-center mb-4 mt-2">
        <h2 className="font-display text-xl font-bold text-slate-800">Il Tuo Diario</h2>
        <p className="text-slate-400 text-xs mt-1">Cronologia ultimi 7 giorni</p>
        <span className="inline-block mt-2 text-[10px] font-semibold text-[#2b90ef] bg-[#e0f2fe] px-2.5 py-1 rounded-full tracking-wide">
          Auto-pulizia attiva · Max 7 giorni
        </span>
      </div>

      <CaloriesTrend meals={meals} target={target} />

      {waterStreak > 0 && (
        <div className="flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 mb-4 bg-[#fff7ed] border border-[#fcd9a5]" data-testid="water-streak">
          <Flame size={18} className="text-[#f59e0b] shrink-0" />
          <p className="text-[12px] text-[#92400e] leading-snug">
            <b>Serie idratazione:</b> {waterStreak} {waterStreak === 1 ? "giorno" : "giorni"} di fila 🔥 — continua così!
          </p>
        </div>
      )}

      <WeightTrend profile={profile} onProfileChange={onProfileChange} />

      {/* Promemoria idratazione discreto */}
      {hydrationReached ? (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 mb-4 bg-[#ecfdf5] border border-emerald-100"
          data-testid="hydration-reminder-banner"
        >
          <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
          <p className="text-[12px] text-emerald-700 leading-snug">
            <b>Obiettivo idratazione raggiunto!</b> Hai bevuto {WATER_TARGET} bicchieri oggi. Ottimo lavoro 💧
          </p>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 mb-4 bg-[#f0fdfa] border border-teal-100"
          data-testid="hydration-reminder-banner"
        >
          <Droplets size={18} className="text-[#2cd4bf] shrink-0" />
          <p className="text-[12px] text-slate-600 leading-snug">
            Ti {glassesLeft === 1 ? "manca" : "mancano"}{" "}
            <b className="text-[#0f766e]" data-testid="hydration-glasses-left">{glassesLeft}</b>{" "}
            {glassesLeft === 1 ? "bicchiere" : "bicchieri"} d'acqua per l'obiettivo di oggi 💧
          </p>
        </motion.div>
      )}

      <div className="bg-white rounded-2xl p-4 mb-4 shadow-[0_4px_12px_rgba(0,0,0,0.03)] border border-slate-200">
        <div className="flex justify-between items-center mb-3">
          <div className="flex-1 text-center">
            <span className="block text-[10px] uppercase tracking-wide text-slate-400 font-bold mb-0.5">Assunte</span>
            <span className="font-display text-xl font-bold text-slate-800" data-testid="cal-consumed">{consumed}</span>
            <span className="text-[11px] text-slate-400 ml-0.5">kcal</span>
          </div>
          <div className="w-px h-8 bg-slate-200" />
          <div className="flex-1 text-center">
            <span className="block text-[10px] uppercase tracking-wide text-slate-400 font-bold mb-0.5">Rimaste</span>
            <span className="font-display text-xl font-bold text-[#2b90ef]" data-testid="cal-remaining">{remaining}</span>
            <span className="text-[11px] text-slate-400 ml-0.5">kcal</span>
          </div>
        </div>
        <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ backgroundColor: barColor }}
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />
        </div>
        <p className="text-[10px] text-slate-400 text-center mt-2">Obiettivo giornaliero: {target} kcal</p>
      </div>

      {/* Riepilogo di oggi con grafici semplici */}
      <div className="bg-white rounded-2xl p-4 mb-4 shadow-[0_4px_12px_rgba(0,0,0,0.03)] border border-slate-200" data-testid="daily-summary">
        <h3 className="font-display text-sm font-bold text-slate-800 mb-3">Riepilogo di Oggi</h3>

        <span className="text-[10px] uppercase tracking-wide text-slate-400 font-bold block mb-1.5">Macronutrienti</span>
        <div className="w-full h-3 rounded-full overflow-hidden flex bg-slate-100 mb-2" data-testid="macro-bar">
          <div style={{ width: `${(kc.c / totalKc) * 100}%`, backgroundColor: "#f59e0b" }} className="h-full" />
          <div style={{ width: `${(kc.f / totalKc) * 100}%`, backgroundColor: "#3b82f6" }} className="h-full" />
          <div style={{ width: `${(kc.p / totalKc) * 100}%`, backgroundColor: "#ef4444" }} className="h-full" />
        </div>
        <div className="flex justify-between">
          {macroBars.map((mb) => (
            <div key={mb.key} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: mb.color }} />
              <span className="text-[11px] text-slate-500">{mb.label} <b className="text-slate-800" data-testid={`macro-${mb.key}`}>{mb.g}g</b></span>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-100 mt-3 pt-3">
          <span className="text-[10px] uppercase tracking-wide text-slate-400 font-bold flex items-center gap-1 mb-2"><Wine size={12} /> Bevande di oggi</span>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-slate-50 border border-slate-100 rounded-xl py-2 text-center">
              <span className="block text-lg font-bold font-display text-slate-800" data-testid="drinks-count">{drinks.length}</span>
              <span className="text-[9px] uppercase tracking-wide text-slate-400">Drink</span>
            </div>
            <div className="bg-slate-50 border border-slate-100 rounded-xl py-2 text-center">
              <span className="block text-lg font-bold font-display text-[#f59e0b]" data-testid="drinks-kcal">{drinkKcal}</span>
              <span className="text-[9px] uppercase tracking-wide text-slate-400">kcal</span>
            </div>
            <div className="bg-slate-50 border border-slate-100 rounded-xl py-2 text-center">
              <span className="block text-lg font-bold font-display text-[#2b90ef]" data-testid="drinks-alcohol">{alcoholG}g</span>
              <span className="text-[9px] uppercase tracking-wide text-slate-400">Alcol</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-[0_4px_12px_rgba(0,0,0,0.02)] mb-5">
        <label className="block text-[10px] text-slate-400 font-bold uppercase mb-1">Obiettivo Attivo</label>
        <select
          data-testid="goal-select"
          value={profile.goal}
          onChange={(e) => onProfileChange({ ...profile, goal: e.target.value })}
          className="w-full bg-transparent text-[15px] text-slate-800 font-medium outline-none cursor-pointer"
        >
          <option value="Perdere Peso">Perdere Peso</option>
          <option value="Mantenimento">Mantenimento Peso</option>
          <option value="Aumento Massa">Aumento Massa Muscolare</option>
          <option value="Generale">Generale / Energia</option>
        </select>
      </div>

      <Beverages
        water={water}
        onWaterChange={setWaterCount}
        onDrinkAdded={() => onMealsChange(loadMeals())}
      />

      <div className="w-full flex flex-col gap-2.5" data-testid="meals-list">
        {meals.length === 0 && (
          <div className="text-slate-400 py-10 text-center text-sm italic" data-testid="empty-diary">
            <Utensils size={28} className="mx-auto mb-2 text-slate-300" />
            Nessun pasto registrato. Scansiona il tuo primo piatto!
          </div>
        )}
        {meals.map((m, i) => (
          <motion.div
            key={m.id}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.04 }}
            onClick={() => setDetailMeal(m)}
            role="button"
            className="flex items-center bg-white rounded-2xl p-3 shadow-[0_2px_8px_rgba(0,0,0,0.03)] border-l-4 cursor-pointer hover:shadow-[0_4px_14px_rgba(43,144,239,0.12)] transition-shadow active:scale-[0.99]"
            style={{ borderColor: mealColor(m.meal_type) }}
            data-testid="meal-card"
          >
            {m.image ? (
              <img src={m.image} alt={m.dish_name} className="w-12 h-12 rounded-xl object-cover mr-3 shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-slate-100 mr-3 flex items-center justify-center shrink-0">
                {m.kind === "drink" ? (
                  <GlassWater size={18} className="text-[#2cd4bf]" />
                ) : (
                  <Utensils size={18} className="text-slate-400" />
                )}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <h3 className="text-sm font-semibold text-slate-800 truncate">{m.dish_name}</h3>
                <span className="text-[10px] text-slate-400 shrink-0">{fmtTime(m.timestamp)}</span>
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                {(m.is_drink || m.kind === "drink" || m.meal_type === "Bevanda") && (
                  <span className="drink-badge" data-testid="drink-badge">
                    <Wine size={9} /> {m.drink_category || "Bevanda"}
                  </span>
                )}
                <p className="text-[11px] text-slate-400 truncate">{m.meal_type} · {fmtDate(m.timestamp)}</p>
                {m.note && <StickyNote size={11} className="text-[#f59e0b] shrink-0" data-testid="meal-note-indicator" />}
                {(m.warning === true || getDietaryWarnings(profile, [m.dish_name, m.drink_category, ...(m.ingredients || [])]).length > 0) && (
                  <span className="flex items-center gap-0.5 text-[10px] font-bold text-[#b45309] bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5 shrink-0" data-testid={`meal-warning-${m.id}`} title="Contiene un alimento del tuo profilo">
                    <AlertTriangle size={10} className="text-[#f59e0b]" />
                  </span>
                )}
              </div>
            </div>
            <div className="text-right ml-2 shrink-0">
              <div className="text-[13px] font-bold flex items-center gap-1" style={{ color: mealColor(m.meal_type) }}>
                <Flame size={12} />{m.calories}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(m.id); }}
                data-testid={`delete-meal-${m.id}`}
                className="text-slate-300 hover:text-red-400 transition-colors mt-1"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      <MealDetail
        meal={detailMeal}
        onClose={() => setDetailMeal(null)}
        onSaveEdit={handleMealEdit}
        onOpenRecipe={(mm) => {
          setDetailMeal(null);
          setRecipeMeal(mm);
        }}
      />

      <RecipeSheet meal={recipeMeal} onClose={() => setRecipeMeal(null)} />
    </div>
  );
};
